/**
 * The BuildBee user guide, as data for the in-app Help page (/help). Text may use **bold** for the
 * names of buttons, fields and tabs. Keep it in step with the app: features behind a release flag
 * that is off (sub pay apps, lien waivers, accounting, bids, meetings) are left out on purpose.
 */

export type GuideBlock =
  | { kind: 'p'; text: string }
  | { kind: 'steps'; items: string[] }
  | { kind: 'bullets'; items: string[] }
  | { kind: 'table'; head: string[]; rows: string[][] };

export type GuideTopic = { id: string; title: string; blocks: GuideBlock[] };
export type GuideSection = { id: string; title: string; lead: string; topics: GuideTopic[] };

const p = (text: string): GuideBlock => ({ kind: 'p', text });
const steps = (...items: string[]): GuideBlock => ({ kind: 'steps', items });
const bullets = (...items: string[]): GuideBlock => ({ kind: 'bullets', items });
const table = (head: string[], ...rows: string[][]): GuideBlock => ({ kind: 'table', head, rows });

export const GUIDE: readonly GuideSection[] = [
  {
    id: 'start',
    title: 'Getting started',
    lead: 'You can be working in BuildBee in about five minutes: create or join a workspace, verify your email, and land on Home. What you see depends on your role; if a button in this guide is missing, ask an owner or admin.',
    topics: [
      {
        id: 'create-workspace',
        title: 'Create a new workspace',
        blocks: [
          steps(
            'Go to the sign-up page (**New to BuildBee? Create an account** on the sign-in screen).',
            'Enter Your name, Work email, a Password of at least 10 characters, Company name, Country / province or state, and Business type (Residential builder, Remodeler, Commercial contractor / GC or Specialty subcontractor).',
            'Click **Create workspace**. A free Premium trial starts with no card needed.',
            'Open the verification email and click its link. If it does not arrive, click **Resend verification email**.',
          ),
          p('Each owner can set up one workspace. A workspace is one company; it can hold as many projects as you need. For a second company, contact BuildBee support.'),
          p('Your business type sets the starter cost codes. Commercial and Specialty sub workspaces also get the Drawings, RFIs and Submittals tabs.'),
        ],
      },
      {
        id: 'join-team',
        title: 'Join a team you were invited to',
        blocks: [
          steps(
            'Open the invitation email and click its link.',
            'If you are signed in, click **Accept invitation**. Otherwise click **Create an account** (your email is filled in for you) or **I already have an account**.',
          ),
        ],
      },
      {
        id: 'sign-in',
        title: 'Sign in',
        blocks: [
          p('On **Sign in to BuildBee**, use Google or Microsoft, or your email and password. You can also choose **Email me a link instead** for a password-free link. Forgot it? Click **Forgot password?** and follow the reset email.'),
          p('**Company code** (required): type the code of your company (for example VANTOM); ask your office if you do not have it. Owners find it in **Settings > General**. Clients who type their contractor code land in its client portal. BuildBee staff and new accounts without a company yet can choose **No company code?**.'),
        ],
      },
      {
        id: 'find-your-way',
        title: 'Find your way around',
        blocks: [
          bullets(
            '**Top bar:** Home, CRM, Projects, Financials, Client portal and Settings. You only see the areas your role can use; a lock icon means your plan does not include it.',
            '**Workspace switcher** (your company name, top left): switch between companies you belong to. Each owner sets up one company; to add another, contact BuildBee support.',
            '**Theme toggle** (sun icon): switch between light and dark.',
            '**Account menu** (your initial, top right): **Profile**, this **User guide**, and **Sign out**.',
          ),
          p('**Home** shows **Needs you** (your tasks overdue or due today, with **Open my day**) and your active projects with progress and contract value.'),
        ],
      },
    ],
  },
  {
    id: 'crm',
    title: 'CRM',
    lead: 'CRM is where work is won: contacts, deals on a pipeline, estimates, takeoffs and signed proposals. Its tabs are My day, Pipeline, Contacts, Subs & vendors, Estimates & proposals and Reports; the More menu holds templates, Find duplicates and Import from CSV.',
    topics: [
      {
        id: 'my-day',
        title: 'Plan your day',
        blocks: [
          p('**My day > Today** shows what is due today and overdue, grouped as Overdue, Today, Next 7 days, No due date and Done today. Side cards flag deals going stale, bids due, deals closing this week and proposals waiting for signature.'),
          p('To add a task, type it in the **New task** row, set Due and Assignee, tick **High** if urgent, and press Enter. **All tasks** lists everyone’s tasks with search, assignee and status filters.'),
        ],
      },
      {
        id: 'contacts',
        title: 'Add contacts and companies',
        blocks: [
          steps(
            'Go to **Contacts > People** and click **New contact**.',
            'Fill in First and Last name, Company, Job title, Emails, Phones, Lead source, Owner, Tags and Notes. A **Possible duplicate** warning appears if the person may already exist.',
            'Tick **Client portal access** to let them sign in to the portal with their primary email.',
            'Click **Save**.',
          ),
          p('For a business, use **Contacts > Companies > New company**. Bring in a list with **More > Import from CSV**, and clean up doubles with **More > Find duplicates > Review & merge**.'),
        ],
      },
      {
        id: 'vendors',
        title: 'Manage subs and vendors',
        blocks: [
          steps(
            'Go to **Subs & vendors** and click **New sub / vendor**.',
            'On **Profile**, add Trades, Service area and Licences.',
            'On **Insurance**, click **Add certificate**: policy type, carrier, policy number, limit, effective and expiry dates (Expires is required), and upload the COI.',
            'Use **Prequalification** for the questionnaire and review, and **Ratings** to rate the vendor.',
          ),
          p('Filter the list by type, trade, CSI division, service area, insurance, prequal or rating. **Notify the team now** sends insurance-expiry alerts.'),
        ],
      },
      {
        id: 'pipeline',
        title: 'Work the pipeline',
        blocks: [
          steps(
            'Go to **Pipeline** and click **New deal**.',
            'Enter the Title (required, for example “Smith kitchen remodel”), then Stage, Contact, Company, Value, Lead source, Expected close, Bid / RFP due, Owner and Tags.',
            'Move a deal by dragging its card to another column, or use the card’s **…** menu.',
            'To close it, drag it onto **Drop to mark won** or **Drop to mark lost**, or open the deal and click **Won** or **Lost**. Won asks for the close date (not in the future), final amount and won reason; Lost asks for the lost reason and competitor.',
            'Switch to **Table** for a sortable list with probability and weighted value. Save filters as a **View**, and **Export** to CSV.',
          ),
          p('A stage can require things before a deal enters it, such as an estimate or a sent proposal. A closed deal can be reopened from its **…** menu. Owners and admins set up stages, reasons and automation under **Pipeline > Pipelines**.'),
        ],
      },
      {
        id: 'convert',
        title: 'Turn a won deal into a project',
        blocks: [
          steps(
            'On the won deal, click **Convert to project**.',
            'Enter the Project name, Project manager and Start date.',
            'Pick the **Estimate for the budget**. BuildBee suggests the accepted estimate; you can also choose “No estimate (use the deal value)”.',
            'Choose whether to attach the deal’s documents and signed proposal, and whether the client gets portal access.',
            'Click **Create project**. A converted deal cannot be reopened.',
          ),
        ],
      },
      {
        id: 'estimates',
        title: 'Build an estimate',
        blocks: [
          steps(
            'Go to **Estimates & proposals > Estimates** and click **New estimate**, or click **New estimate** on a deal.',
            'Choose the Deal (optional), Title, Client, and **Start from** a saved template or a blank estimate. Click **Create**.',
            'Set **Default markup %** and **Tax %** at the top.',
            'Add lines: Description, Qty, Unit, Unit cost and Markup % (blank uses the default). Press **Enter** in Unit cost to start the next line.',
            'Click the arrow at the left of a line to open its details: Section, **Cost code**, Category, Note, and the Taxable, Allowance and Optional boxes. Optional lines are not counted in the total.',
            'Add **Notes (shown on the proposal)**, Terms and Valid until, then click **Save**. Every line needs a description first.',
          ),
          p('The side panel shows Cost, Markup, Subtotal, Tax, Total and Margin, plus a **By cost code** summary. Other buttons: **Import lines** (CSV, up to 1,000 lines), **CSV** download, **Print** and **Takeoff**.'),
        ],
      },
      {
        id: 'estimate-status',
        title: 'Estimate statuses and revisions',
        blocks: [
          p('An estimate moves through Draft, In review, Sent, Accepted and Declined. Only owners, admins and sales can mark an estimate accepted. Sending a proposal marks it Sent, and a signature marks it Accepted.'),
          p('An accepted estimate, or one used as a project budget, is **locked**. To change it, click **New revision**: BuildBee copies it (for example EST-0004-R2) and marks the old one Superseded.'),
        ],
      },
      {
        id: 'takeoff',
        title: 'Measure with takeoff',
        blocks: [
          steps(
            'On the estimate, click **Takeoff**, then **Add sheet** (upload a PDF or image, or pick a project drawing).',
            'Click **Calibrate** or choose a standard scale. You must set a scale first.',
            'Measure with **Linear**, **Area** or **Count**. Give each measurement a name, cost code, multiplier, waste % and unit.',
            'Click **Send to estimate** to create or update estimate lines from the measurements.',
          ),
        ],
      },
      {
        id: 'proposals',
        title: 'Send a proposal and get it signed',
        blocks: [
          steps(
            'Save the estimate, then click **Create proposal** (the estimate needs a client).',
            'On the **Edit** tab, set the Title, Cover letter and scope, Valid until and **Deposit due at signing**, or start from a template. Check the **Preview** tab.',
            'Click **Save**, then **Send**. Enter To, Subject and Message.',
            'The client opens the link, no sign-in needed, and clicks **Accept and sign** with their name and a drawn or typed signature. They can also decline with a note.',
            'Once signed, download the **Signed copy**. BuildBee then offers to mark the deal won.',
          ),
          p('Made an estimate or proposal outside the pipeline? Open it and click **Link to deal** to attach it, with its revisions, to an open deal.'),
          p('A sent proposal cannot be edited. Use **New signing link** to replace a lost link, **Void** to cancel it, or **Re-open** a declined, void or expired proposal as a draft.'),
        ],
      },
      {
        id: 'crm-reports',
        title: 'CRM reports',
        blocks: [
          p('**Reports** shows open pipeline, win rate, average won deal, sales cycle and repeat clients for a period, pipeline and owner, with charts for value by stage, conversion, forecast, lost reasons, lead sources and client lifetime value.'),
        ],
      },
    ],
  },
  {
    id: 'projects',
    title: 'Projects',
    lead: 'A project holds everything about one job once it is won: schedule, team, chat, documents, drawings, field reports, photos, punch list and time. Most projects start from Convert to project on a won deal.',
    topics: [
      {
        id: 'new-project',
        title: 'Create a project by hand',
        blocks: [
          steps(
            'Go to **Projects** and click **New project** (owners, admins, project managers and sales).',
            'Enter the Project name, Type and Status, and pick a **Template** to create schedule phases for you.',
            'Set the Project manager, Start date, Target completion, Contract value and Description.',
            'Click **Create project**.',
          ),
          p('Statuses: Planning, Preconstruction, Active, On hold, Punch, Completed, Closed, Cancelled.'),
        ],
      },
      {
        id: 'project-tabs',
        title: 'Project tabs',
        blocks: [
          table(
            ['Tab', 'What you do there'],
            ['Overview', 'Health alerts, the next two weeks, recent photos and key stats. **Edit project** changes details, site location and geofence'],
            ['Schedule', '3-week lookahead, Gantt or List. **Add task** with dates, assignee, predecessors and **Show in client portal**'],
            ['Team', '**Add** people or contacts with a project role, and fine-tune their access'],
            ['Chat', 'Direct messages and channels; attach files; turn a message into a punch item'],
            ['Documents', '**Upload to folder**, keep versions, **Share with client**'],
            ['Drawings', 'Upload sets, mark up sheets, pin RFIs or punch items (Commercial and Specialty sub)'],
            ['RFIs', '**New RFI**, set ball in court and due date, record the official answer (Commercial and Specialty sub)'],
            ['Submittals', 'Submit, review and revise submittals (Commercial and Specialty sub)'],
            ['Daily logs', 'Weather, crews, work, delays, deliveries, safety and photos; submit for approval'],
            ['Photos', 'Upload, organize into albums, caption, tag and share with the client'],
            ['Punch list', '**Add item**, assign it, then Start work, Ready for review, Close'],
            ['Time', 'Approved hours, labour cost, and hours by worker and **by cost code**'],
          ),
        ],
      },
      {
        id: 'daily-log',
        title: 'Write a daily log',
        blocks: [
          steps(
            'Open the project’s **Daily logs** tab and click **New daily log**.',
            'Pick the date and click **Start log**. Crews are copied from the previous log.',
            'Fill in Weather, crews (**Add crew**), Work performed, Delays, Deliveries, Visitors & inspections and Safety notes. Add photos.',
            'Tick **Show this log in the client portal** if the client should see it, then **Save log** and **Submit**.',
            'An approver clicks **Approve** or **Send back** with what needs to change.',
          ),
        ],
      },
      {
        id: 'time-clock',
        title: 'Track time with the clock',
        blocks: [
          steps(
            'Click **Clock in** on the Projects page.',
            'Choose the Project (or shop / office time) and the **Cost code**, and add notes. Outside the site geofence the entry shows an **Off site** badge.',
            'Click **Clock out** at the end, entering your break minutes.',
            'Submit your week on **Timesheets**. A manager clicks **Approve** or **Reject** (a reason is required).',
          ),
        ],
      },
      {
        id: 'rfi-from-drawing',
        title: 'Raise an RFI from a drawing',
        blocks: [
          steps(
            'Open **Drawings** and click a sheet.',
            'Choose the **Pin** tool and click the spot in question, then **Pin an RFI here**.',
            'Fill in Subject, Question, Priority, Due and **Ball in court**, then click **Send RFI**.',
            'When the answer comes in, write it and tick **This is the official answer**.',
          ),
        ],
      },
    ],
  },
  {
    id: 'financials',
    title: 'Financials',
    lead: 'Financials holds every project’s money in one place: budget, subcontracts and POs, change orders, invoices and payments. Each project’s financial page shows Revised contract, Revised budget, Cost to date, Projected margin, Billed / paid and Left to bill (the contract not yet invoiced, with unpaid invoices and retainage underneath), with Budget, Change orders, Billing and Subcontracts & POs tabs.',
    topics: [
      {
        id: 'budget',
        title: 'Build the project budget',
        blocks: [
          p('The fastest route is **Convert to project** on a won deal with an **Estimate for the budget**: every non-optional estimate line becomes a budget line at cost. To build or adjust it by hand:'),
          steps(
            'Open the project’s **Budget** tab.',
            'Click **Add from cost codes**, search the library, tick the codes you need and click **Add N lines**. Lines start at $0.',
            'Or click **+ Add line** and fill Cost code, Category, Description and Original budget.',
            'Pick a **Forecast method** (Auto, Remaining budget, Committed cost or Cost performance) to drive the Projected column.',
          ),
        ],
      },
      {
        id: 'commitments',
        title: 'Subcontracts and purchase orders',
        blocks: [
          steps(
            'Go to **Subcontracts & POs** and click **+ New**.',
            'Choose the Project and Type, then enter a Title and the Vendor (both required).',
            'Set Retainage %, dates, Scope / notes and the lines. Leave **Budget line** empty to link each line by its cost code.',
            'Click **Mark sent for signature**, then **Execute**. Executing locks the schedule of values and adds it to committed cost.',
            'When the work is done, click **Close out**.',
          ),
        ],
      },
      {
        id: 'change-orders',
        title: 'Change orders',
        blocks: [
          steps(
            'In the project’s **Change orders** tab, click **New change event**.',
            'Enter the Title, Reason and Scope (To be determined, Out of scope (billable), In scope (not billable) or Allowance), and the lines.',
            'Click **Convert to change orders**: a prime change order to the client (at price) and commitment change orders to subs (at cost), created as drafts.',
            'Open the change order and click **Submit for review**, then **Send to client**.',
            'Click **Record approval** (Signed by is required) or **Record rejection**.',
          ),
          p('When a change order is approved, each line joins the budget line with the same cost code, or a new one is created.'),
        ],
      },
      {
        id: 'invoices',
        title: 'Create and send an invoice',
        blocks: [
          steps(
            'Go to **Invoices** and click **+ New > Invoice**.',
            'Choose the Project (optional) and **Bill to** (required), and pick the Type: Invoice, Deposit, Change order billing or Final invoice.',
            'Check the Issue and Due dates, Tax rate % and Retainage %.',
            'Add lines: Description, Cost code, Qty, Unit, Unit price, Tax.',
            'Leave **Allow card payment** and **Allow ACH payment** on if the client may pay online.',
            'Click **Save draft**, review it, then click **Send**. The client gets a link to view and pay in the portal.',
          ),
          p('A project’s invoices can never add up to more than its revised contract (before tax). To bill more, approve a change order to raise the contract first. An invoice with payments must be refunded before it can be voided.'),
        ],
      },
      {
        id: 'pay-apps',
        title: 'Progress billing (AIA pay applications)',
        blocks: [
          steps(
            'In the project’s **Billing** tab, click **+ New > Pay application (AIA G702/G703)**.',
            'The schedule of values fills itself from the budget cost codes and approved change orders.',
            'Set the period, dates and retainage % on work and stored materials.',
            'For each line, enter This period and Stored, or type a **% to date**.',
            'Check **Amount due this application**, then save. Tick **Final pay application** on the last one.',
          ),
          p('To pay out retainage later, use **+ New > Retainage release**.'),
        ],
      },
      {
        id: 'payments',
        title: 'Record a payment',
        blocks: [
          p('On the invoice, click **Record payment** and enter the Amount, Method, Received on and Reference. Card and ACH payments made in the portal are recorded automatically.'),
        ],
      },
      {
        id: 'fin-reports',
        title: 'Reports',
        blocks: [
          table(
            ['Report', 'What it answers'],
            ['Job cost', 'Budget vs actual by cost code, grouped by division'],
            ['WIP', 'Over or under billing on open jobs'],
            ['AR aging', 'What clients owe, by 30-day bucket'],
            ['Change order log', 'Prime and commitment change orders and how long each has been outstanding'],
          ),
        ],
      },
    ],
  },
  {
    id: 'cost-codes',
    title: 'Cost codes',
    lead: 'A cost code labels what a dollar is for, such as 09 91 00 Painting. Use the same code at every step, from estimate to budget to subcontract to invoice, and BuildBee lines the numbers up for you.',
    topics: [
      {
        id: 'library',
        title: 'The library',
        blocks: [
          p('BuildBee ships with the **CSI MasterFormat 2018** list in the format **DD SS SS** (division, section, subsection), with every division heading and about 90 common sections. Your business type decides which divisions you see:'),
          table(['Business type', 'Divisions included'], ['Residential', '01–12, 22, 23, 26, 27, 31–33'], ['Remodeler', '01, 02, 06–12, 22, 23, 26'], ['Commercial', 'All divisions'], ['Specialty sub', '01, 21–23, 25–28']),
          p('Go to **Financials > Cost codes** to browse. **Show seed for** previews the list for another business type.'),
        ],
      },
      {
        id: 'add-code',
        title: 'Add your own cost code',
        blocks: [
          steps(
            'On the **Workspace codes** tab, click **Add cost code**.',
            'Enter the **Code** (up to 40 characters) and the **Name**.',
            'Optionally set the Division, **Default category**, a **Parent code** to nest it, and a Description.',
            'Click **Save**.',
          ),
          p('Six-digit codes are saved as “09 21 16” however you type them; other codes are uppercased. To rename a MasterFormat section, add a workspace code with the same number. Archive codes you no longer use: old lines keep them, and you can restore them later.'),
        ],
      },
      {
        id: 'where-used',
        title: 'Where you use cost codes',
        blocks: [
          table(
            ['Where', 'How to set it', 'What it gives you'],
            ['Estimate lines', 'Open the line (arrow) > Cost code', '“By cost code” cost and price summary'],
            ['Takeoff', 'On each measurement', 'Quantities by cost code'],
            ['Project budget', 'Cost code column, or Add from cost codes', 'Budget vs actual per code'],
            ['Subcontracts and POs', 'Line cost code; leave Budget line empty', 'Committed cost lands on the right budget line'],
            ['Change orders', 'Line cost code', 'Approved changes join the matching budget line'],
            ['Invoices and pay apps', 'Line cost code', 'Billing by code'],
            ['Time clock', 'Cost code when clocking in', 'Labour by cost code'],
          ),
        ],
      },
      {
        id: 'matching',
        title: 'How lines find their budget line',
        blocks: [
          p('When a subcontract or PO is executed or a change order is approved, each line with an empty **Budget line** is matched by its cost code:'),
          steps(
            'A budget line was picked: the line uses it.',
            'No cost code: the line goes to an uncoded line for its category.',
            'The code is on the budget: the line joins that budget line.',
            'The code is not on the budget yet: BuildBee creates a $0 budget line with that code.',
          ),
          p('Codes typed in different styles still match: “092116”, “09-21-16” and “09 21 16” are the same. Check **Financials > Reports > Job cost** for Projected and Variance by code.'),
        ],
      },
    ],
  },
  {
    id: 'portal-settings',
    title: 'Client portal and Settings',
    lead: 'The client portal gives each client a private, branded page for their project, with no password. Settings is where owners and admins set up the company, the team and the defaults everything else uses.',
    topics: [
      {
        id: 'share-project',
        title: 'Share a project with a client',
        blocks: [
          steps(
            'Go to **Client portal** and click **Share a project**.',
            'Pick the Project and the Contact (they need an email address).',
            'Leave **Email an invitation now** ticked and click **Share and invite**.',
            'Use **Change what is shared** to choose what they see, and **Preview as client** to check it.',
          ),
          p('The client signs in with an emailed link that lasts 15 minutes. They can approve change orders, pick selections, pay invoices and message you; replies go through **Client portal > Inbox**.'),
        ],
      },
      {
        id: 'settings',
        title: 'Settings',
        blocks: [
          table(
            ['Page', 'What to set'],
            ['Company details', 'Company name; country, tax, currency, time zone; payment terms; default markup, tax and retainage %; number prefixes'],
            ['Branding', 'Logo, colours, portal welcome message, email footer'],
            ['Members', '**Invite** people with a role; change roles, suspend or remove'],
            ['Roles & permissions', 'What each role can do, plus exceptions for individual people'],
            ['Plan & billing', 'Basic or Premium; **Manage billing** (owner only)'],
            ['Custom fields', '**Add field** to contacts, deals, projects, estimates, invoices and more'],
            ['Activity log', 'Who changed what and when'],
          ),
        ],
      },
    ],
  },
  {
    id: 'cheat-sheet',
    title: 'Cheat sheet',
    lead: 'The whole job in one line: Contact > Deal > Estimate > Proposal > Signed > Won > Convert to project > Budget > Subs and POs > Change orders > Invoices > Paid.',
    topics: [
      {
        id: 'common-tasks',
        title: 'Common tasks',
        blocks: [
          table(
            ['I want to…', 'Go to', 'Click'],
            ['See what is due today', 'Home or CRM > My day', 'Open my day'],
            ['Add a lead', 'CRM > Contacts > People', 'New contact'],
            ['Start a deal', 'CRM > Pipeline', 'New deal'],
            ['Close a deal', 'Deal page', 'Won or Lost'],
            ['Price a job', 'CRM > Estimates', 'New estimate, then Save'],
            ['Add the next estimate line fast', 'Estimate, Unit cost box', 'Press Enter'],
            ['Set a line’s cost code', 'Estimate line', 'Arrow at the left of the line'],
            ['Change a locked estimate', 'Estimate', 'New revision'],
            ['Send for signature', 'Estimate', 'Create proposal > Save > Send'],
            ['Start the job', 'Won deal', 'Convert to project'],
            ['Fill the budget', 'Project financials > Budget', 'Add from cost codes'],
            ['Hire a sub', 'Financials > Subcontracts & POs', '+ New, then Execute'],
            ['Price a change', 'Project financials > Change orders', 'New change event'],
            ['Bill the client', 'Financials > Invoices', '+ New > Invoice > Send'],
            ['Progress bill (AIA)', 'Project financials > Billing', '+ New > Pay application'],
            ['Log a payment', 'Invoice', 'Record payment'],
            ['Clock in on site', 'Projects', 'Clock in'],
            ['Show the client their job', 'Client portal', 'Share a project'],
            ['Add a cost code', 'Financials > Cost codes', 'Add cost code'],
            ['Check budget vs actual', 'Financials > Reports', 'Job cost'],
            ['Invite a coworker', 'Settings > Members', 'Invite'],
          ),
        ],
      },
      {
        id: 'statuses',
        title: 'Status at a glance',
        blocks: [
          table(
            ['Record', 'Statuses, in order'],
            ['Estimate', 'Draft > In review > Sent > Accepted or Declined (Superseded after a revision)'],
            ['Proposal', 'Draft > Sent > Viewed > Signed or Declined (also Expired, Void)'],
            ['Change order', 'Draft > Internal review > Sent to client > Approved or Rejected'],
            ['Subcontract or PO', 'Draft > Sent for signature > Executed > Closed'],
            ['Invoice', 'Draft > Sent > Viewed > Partially paid > Paid (also Overdue, Void)'],
            ['Project', 'Planning > Preconstruction > Active > Punch > Completed > Closed'],
          ),
        ],
      },
      {
        id: 'cost-code-rules',
        title: 'Cost code rules of thumb',
        blocks: [
          bullets(
            'Format **DD SS SS**, for example 09 91 00 Painting.',
            'Use one code for the same work everywhere: estimate, budget, subcontract, change order, invoice and time.',
            'Leave **Budget line** empty on subs, POs and change orders; the cost code finds it.',
            'Check **Reports > Job cost** weekly for Projected and Variance by code.',
          ),
        ],
      },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    lead: 'Most blocks protect signed or shared work, and the message tells you the next step.',
    topics: [
      {
        id: 'messages',
        title: 'Common messages',
        blocks: [
          table(
            ['You see', 'What to do'],
            ['“Estimate X is accepted. Create a revision to change it.”', 'Click **New revision** and edit the copy'],
            ['“Proposal P was sent with this estimate…”', 'Create a revision, or void the proposal'],
            ['“Every line needs a description first.”', 'Fill in or delete the empty line, then save'],
            ['“A sent proposal cannot be edited.”', '**Re-open** it as a draft, or void it'],
            ['“This invoice would bill … more than the contract”', 'Approve a change order to raise the contract, or bill less'],
            ['“Set a scale first” (takeoff)', 'Click **Calibrate** or pick a standard scale'],
            ['“Before moving to <Stage>, …”', 'Add what the stage requires, such as an estimate'],
            ['“Mark the deal as won before converting it to a project”', 'Click **Won** first'],
            ['“Invoices with payments must be refunded first.”', 'Refund the payment, then void'],
            ['“Cost code X … is archived”', 'Tick **Show archived** and restore it'],
            ['A menu item or button is missing', 'Ask an owner or admin about your role or plan'],
            ['Someone else changed this record', 'Reload the page and redo your change'],
          ),
        ],
      },
    ],
  },
];
